import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	treatmentPlanService,
	GenerateSmartProgramPayload,
} from "../../services/treatmentPlan.service";
import { TreatmentPlanRequest } from "../../domain/treatmentPlan.interface";

export const useGetTreatmentPlans = (treatmentPlanId?: string) => {
	return useQuery({
		queryKey: ["sims-treatment-plans", treatmentPlanId],
		queryFn: () => treatmentPlanService.getTreatmentPlans(treatmentPlanId),
		staleTime: 1000 * 60 * 5,
	});
};

export const useGetTreatmentPlan = (treatmentPlanId: string) => {
	const query = useQuery({
		queryKey: ["sims-treatment-plan", treatmentPlanId],
		queryFn: () => treatmentPlanService.getTreatmentPlan(treatmentPlanId),
		enabled: !!treatmentPlanId,
		staleTime: 1000 * 60 * 5,
	});

	return {
		...query,
		refetch: query.refetch,
	};
};

export const useCreateTreatmentPlan = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (treatmentPlan: TreatmentPlanRequest) =>
			treatmentPlanService.createTreatmentPlan(treatmentPlan),
		onSuccess: () => {
			// Invalidate and refetch treatment plans queries
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plans"],
			});
		},
	});
};

export const useUpdateTreatmentPlan = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: TreatmentPlanRequest }) =>
			treatmentPlanService.updateTreatmentPlan(id, data),
		onSuccess: () => {
			// Invalidate and refetch treatment plans queries
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plans"],
			});
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plan"],
			});
		},
	});
};

export const useGenerateSmartProgram = () => {
	return useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: string;
			payload: GenerateSmartProgramPayload;
		}) => treatmentPlanService.generateSmartProgram(id, payload),
	});
};

export const usePollGenerationStatus = (
	treatmentPlanId: string,
	enabled: boolean,
) => {
	return useQuery({
		queryKey: ["smart-program-generation-status", treatmentPlanId],
		queryFn: () => treatmentPlanService.getGenerationStatus(treatmentPlanId),
		enabled,
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			// Stop polling once the job is done
			if (status === "SUCCESS" || status === "FAILED") {
				return false;
			}
			return 3000; // Poll every 3 seconds
		},
	});
};

export const useGetRequiredFields = (treatmentPlanId: string) => {
	return useQuery({
		queryKey: ["sims-required-fields", treatmentPlanId],
		queryFn: () => treatmentPlanService.getRequiredFields(treatmentPlanId),
		enabled: !!treatmentPlanId,
		staleTime: 1000 * 60 * 5,
	});
};

export const useDeleteTreatmentPlan = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (treatmentPlanId: string) =>
			treatmentPlanService.deleteTreatmentPlan(treatmentPlanId),
		onSuccess: () => {
			// Invalidate and refetch treatment plans queries
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plans"],
			});
		},
	});
};
